import React from "react";
import { Box } from "@chakra-ui/react";

export const MagicWorldBackground: React.FC = () => {
  return (
    <Box
      position="absolute"
      top="0"
      left="0"
      width="100%"
      height="100%"
      overflow="hidden"
      zIndex="0"
      pointerEvents="none"
    >
      {/* Deep magical blue/purple gradient */}
      <Box
        position="absolute"
        width="100%"
        height="100%"
        background="linear-gradient(180deg, #0d0221 0%, #1a0a3e 25%, #2d1b5e 50%, #1a0a3e 75%, #0d0221 100%)"
      />

      {/* Magical elements SVG */}
      <svg
        className="magic-world-svg"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      >
        <defs>
          {/* Star glow gradient */}
          <radialGradient id="starGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
            <stop offset="50%" stopColor="#ffffd0" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#ffff80" stopOpacity="0" />
          </radialGradient>

          {/* Magic wand gradient */}
          <linearGradient id="wandGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8b4513" stopOpacity="1" />
            <stop offset="100%" stopColor="#654321" stopOpacity="1" />
          </linearGradient>

          {/* Wand tip glow */}
          <radialGradient id="wandTipGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
            <stop offset="30%" stopColor="#ffd700" stopOpacity="0.9" />
            <stop offset="70%" stopColor="#ff69b4" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#9400d3" stopOpacity="0" />
          </radialGradient>

          {/* Magic sparkle filter */}
          <filter id="magicSparkle" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background stars - small twinkling */}
        <g className="bg-stars">
          {/* Tiny stars scattered */}
          <circle cx="50" cy="80" r="1.5" fill="#ffffff" className="tiny-star-1" />
          <circle cx="120" cy="150" r="1" fill="#ffffd0" className="tiny-star-2" />
          <circle cx="200" cy="60" r="1.5" fill="#ffffff" className="tiny-star-3" />
          <circle cx="280" cy="200" r="1" fill="#ffffd0" className="tiny-star-4" />
          <circle cx="350" cy="100" r="1.5" fill="#ffffff" className="tiny-star-5" />
          <circle cx="420" cy="180" r="1" fill="#ffffd0" className="tiny-star-6" />
          <circle cx="500" cy="50" r="1.5" fill="#ffffff" className="tiny-star-7" />
          <circle cx="580" cy="140" r="1" fill="#ffffd0" className="tiny-star-8" />
          <circle cx="650" cy="220" r="1.5" fill="#ffffff" className="tiny-star-9" />
          <circle cx="720" cy="70" r="1" fill="#ffffd0" className="tiny-star-10" />
          <circle cx="800" cy="160" r="1.5" fill="#ffffff" className="tiny-star-11" />
          <circle cx="880" cy="90" r="1" fill="#ffffd0" className="tiny-star-12" />
          <circle cx="950" cy="200" r="1.5" fill="#ffffff" className="tiny-star-13" />
          <circle cx="1020" cy="120" r="1" fill="#ffffd0" className="tiny-star-14" />
          <circle cx="1100" cy="180" r="1.5" fill="#ffffff" className="tiny-star-15" />
          <circle cx="1180" cy="60" r="1" fill="#ffffd0" className="tiny-star-16" />
          <circle cx="1250" cy="150" r="1.5" fill="#ffffff" className="tiny-star-17" />
          <circle cx="1320" cy="100" r="1" fill="#ffffd0" className="tiny-star-18" />
          <circle cx="1400" cy="190" r="1.5" fill="#ffffff" className="tiny-star-19" />
          {/* More stars in middle/bottom */}
          <circle cx="80" cy="400" r="1" fill="#ffffff" className="tiny-star-20" />
          <circle cx="160" cy="550" r="1.5" fill="#ffffd0" className="tiny-star-21" />
          <circle cx="240" cy="700" r="1" fill="#ffffff" className="tiny-star-22" />
          <circle cx="320" cy="480" r="1.5" fill="#ffffd0" className="tiny-star-23" />
          <circle cx="400" cy="620" r="1" fill="#ffffff" className="tiny-star-24" />
          <circle cx="480" cy="350" r="1.5" fill="#ffffd0" className="tiny-star-25" />
          <circle cx="560" cy="750" r="1" fill="#ffffff" className="tiny-star-26" />
          <circle cx="640" cy="500" r="1.5" fill="#ffffd0" className="tiny-star-27" />
          <circle cx="720" cy="680" r="1" fill="#ffffff" className="tiny-star-28" />
          <circle cx="800" cy="420" r="1.5" fill="#ffffd0" className="tiny-star-29" />
          <circle cx="880" cy="580" r="1" fill="#ffffff" className="tiny-star-30" />
          <circle cx="960" cy="720" r="1.5" fill="#ffffd0" className="tiny-star-31" />
          <circle cx="1040" cy="450" r="1" fill="#ffffff" className="tiny-star-32" />
          <circle cx="1120" cy="600" r="1.5" fill="#ffffd0" className="tiny-star-33" />
          <circle cx="1200" cy="380" r="1" fill="#ffffff" className="tiny-star-34" />
          <circle cx="1280" cy="530" r="1.5" fill="#ffffd0" className="tiny-star-35" />
          <circle cx="1360" cy="700" r="1" fill="#ffffff" className="tiny-star-36" />
        </g>

        {/* Large decorative stars */}
        <g className="large-stars" filter="url(#magicSparkle)">
          {/* 4-pointed stars */}
          <polygon
            className="big-star-1"
            points="150,300 155,290 160,300 170,305 160,310 155,320 150,310 140,305"
            fill="url(#starGlow)"
          />
          <polygon
            className="big-star-2"
            points="400,150 407,135 414,150 430,158 414,166 407,180 400,166 385,158"
            fill="url(#starGlow)"
          />
          <polygon
            className="big-star-3"
            points="750,250 758,232 766,250 785,260 766,270 758,288 750,270 732,260"
            fill="url(#starGlow)"
          />
          <polygon
            className="big-star-4"
            points="1100,350 1108,332 1116,350 1135,360 1116,370 1108,388 1100,370 1082,360"
            fill="url(#starGlow)"
          />
          <polygon
            className="big-star-5"
            points="1300,200 1306,188 1312,200 1325,206 1312,212 1306,225 1300,212 1288,206"
            fill="url(#starGlow)"
          />
          <polygon
            className="big-star-6"
            points="250,600 256,588 262,600 275,606 262,612 256,625 250,612 238,606"
            fill="url(#starGlow)"
          />
          <polygon
            className="big-star-7"
            points="600,700 608,682 616,700 635,710 616,720 608,738 600,720 582,710"
            fill="url(#starGlow)"
          />
          <polygon
            className="big-star-8"
            points="1000,650 1007,635 1014,650 1030,658 1014,666 1007,680 1000,666 985,658"
            fill="url(#starGlow)"
          />
        </g>

        {/* Magic wands */}
        <g className="magic-wands">
          {/* Wand 1 - top left */}
          <g className="wand-group-1">
            <rect x="100" y="180" width="8" height="60" rx="2" fill="url(#wandGrad)" transform="rotate(-30 104 210)" />
            <circle cx="92" cy="168" r="15" fill="url(#wandTipGlow)" className="wand-tip-1" />
            <circle cx="92" cy="168" r="5" fill="#ffffff" />
          </g>

          {/* Wand 2 - top center */}
          <g className="wand-group-2">
            <rect x="680" y="100" width="7" height="55" rx="2" fill="url(#wandGrad)" transform="rotate(15 683 127)" />
            <circle cx="695" cy="92" r="14" fill="url(#wandTipGlow)" className="wand-tip-2" />
            <circle cx="695" cy="92" r="4" fill="#ffffff" />
          </g>

          {/* Wand 3 - right side */}
          <g className="wand-group-3">
            <rect x="1250" y="280" width="8" height="58" rx="2" fill="url(#wandGrad)" transform="rotate(-45 1254 309)" />
            <circle cx="1232" cy="262" r="15" fill="url(#wandTipGlow)" className="wand-tip-3" />
            <circle cx="1232" cy="262" r="5" fill="#ffffff" />
          </g>

          {/* Wand 4 - bottom left */}
          <g className="wand-group-4">
            <rect x="180" y="650" width="7" height="52" rx="2" fill="url(#wandGrad)" transform="rotate(25 183 676)" />
            <circle cx="198" cy="642" r="13" fill="url(#wandTipGlow)" className="wand-tip-4" />
            <circle cx="198" cy="642" r="4" fill="#ffffff" />
          </g>

          {/* Wand 5 - bottom right */}
          <g className="wand-group-5">
            <rect x="1150" y="600" width="7" height="50" rx="2" fill="url(#wandGrad)" transform="rotate(-20 1153 625)" />
            <circle cx="1142" cy="588" r="12" fill="url(#wandTipGlow)" className="wand-tip-5" />
            <circle cx="1142" cy="588" r="4" fill="#ffffff" />
          </g>
        </g>

        {/* Magic sparkles/particles */}
        <g className="magic-sparkles" filter="url(#magicSparkle)">
          <circle cx="110" cy="175" r="3" fill="#ffd700" className="sparkle-1" />
          <circle cx="85" cy="160" r="2" fill="#ff69b4" className="sparkle-2" />
          <circle cx="100" cy="150" r="2.5" fill="#00ffff" className="sparkle-3" />

          <circle cx="705" cy="85" r="2.5" fill="#ffd700" className="sparkle-4" />
          <circle cx="685" cy="78" r="2" fill="#ff69b4" className="sparkle-5" />
          <circle cx="710" cy="100" r="2" fill="#00ffff" className="sparkle-6" />

          <circle cx="1220" cy="250" r="3" fill="#ffd700" className="sparkle-7" />
          <circle cx="1245" cy="255" r="2" fill="#ff69b4" className="sparkle-8" />
          <circle cx="1225" cy="275" r="2.5" fill="#00ffff" className="sparkle-9" />

          <circle cx="210" cy="635" r="2.5" fill="#ffd700" className="sparkle-10" />
          <circle cx="190" cy="650" r="2" fill="#ff69b4" className="sparkle-11" />
          <circle cx="205" cy="655" r="2" fill="#00ffff" className="sparkle-12" />

          <circle cx="1130" cy="580" r="2.5" fill="#ffd700" className="sparkle-13" />
          <circle cx="1150" cy="595" r="2" fill="#ff69b4" className="sparkle-14" />
          <circle cx="1135" cy="600" r="2" fill="#00ffff" className="sparkle-15" />
        </g>
      </svg>

      {/* Floating magic dust */}
      <Box className="magic-dust" position="absolute" width="100%" height="100%" top="0" left="0">
        {[...Array(20)].map((_, i) => (
          <Box
            key={i}
            className={`dust-particle dust-particle-${i + 1}`}
            position="absolute"
            width={3 + (i % 3) * 2 + "px"}
            height={3 + (i % 3) * 2 + "px"}
            borderRadius="50%"
            backgroundColor={
              i % 4 === 0 ? "#ffd700" :
              i % 4 === 1 ? "#ff69b4" :
              i % 4 === 2 ? "#00ffff" :
              "#ffffff"
            }
            boxShadow={`0 0 ${4 + i % 3 * 2}px ${
              i % 4 === 0 ? "#ffd700" :
              i % 4 === 1 ? "#ff69b4" :
              i % 4 === 2 ? "#00ffff" :
              "#ffffff"
            }`}
            opacity="0"
          />
        ))}
      </Box>

      {/* Ambient magical glow spots */}
      <Box
        className="magic-glow-1"
        position="absolute"
        top="20%"
        left="10%"
        width="200px"
        height="200px"
        borderRadius="50%"
        background="radial-gradient(circle, rgba(148,0,211,0.15) 0%, transparent 70%)"
      />
      <Box
        className="magic-glow-2"
        position="absolute"
        top="60%"
        right="15%"
        width="180px"
        height="180px"
        borderRadius="50%"
        background="radial-gradient(circle, rgba(255,105,180,0.12) 0%, transparent 70%)"
      />
      <Box
        className="magic-glow-3"
        position="absolute"
        bottom="20%"
        left="30%"
        width="150px"
        height="150px"
        borderRadius="50%"
        background="radial-gradient(circle, rgba(0,255,255,0.1) 0%, transparent 70%)"
      />
    </Box>
  );
};

export default MagicWorldBackground;
